import { useMutation } from '@apollo/client'
import { FunctionComponent, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Button, NotificationType, TextField } from '../../../../components'
import { UpdateCreatorResponse, UPDATE_CREATOR } from '../../../../gql'
import { Creator, MutationUpdateCreatorArgs } from '../../../../gql/types'
import { AnalyticsEvent, trackEvent } from '../../../../utils/analytics'
import { useNotification } from '../../../../utils/hooks'
import {
  validateAndCleanSocialMediaInput,
  validateAndCleanOtherSocialMediaLink,
} from '../../../../utils/helpers'

export type EditSocialMediaFormData = {
  instagramHandle: string
  tiktokHandle: string
  canvaLink: string
  otherSocialMediaLink: string
}

interface IProps {
  creator: Creator
}

const EditSocialMedia: FunctionComponent<IProps> = ({ creator }) => {
  const { addNotification } = useNotification()
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({})

  const [updateCreator, { loading }] = useMutation<
    UpdateCreatorResponse,
    MutationUpdateCreatorArgs
  >(UPDATE_CREATOR)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditSocialMediaFormData>({
    defaultValues: {
      instagramHandle: creator.instagramHandle ?? '',
      tiktokHandle: creator.tiktokHandle ?? '',
      canvaLink: creator.canvaLink ?? '',
      otherSocialMediaLink: creator.otherSocialMediaLink ?? '',
    },
    mode: 'onSubmit',
  })

  useEffect(() => {
    trackEvent(AnalyticsEvent.viewCreatorsEditSocialMedia)
  }, [])

  const onSubmit = async (data: EditSocialMediaFormData) => {
    try {
      if (!data) return

      // Reset errors
      setValidationErrors({})

      // Instagram
      const instagramResult = validateAndCleanSocialMediaInput(
        data.instagramHandle,
        'instagram',
      )
      if (!instagramResult.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          instagramHandle: instagramResult.error,
        }))
        return
      }

      // TikTok
      const tiktokResult = validateAndCleanSocialMediaInput(
        data.tiktokHandle,
        'tiktok',
      )
      if (!tiktokResult.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          tiktokHandle: tiktokResult.error,
        }))
        return
      }

      // Canva
      const canvaResult = validateAndCleanOtherSocialMediaLink(data.canvaLink)
      if (!canvaResult.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          canvaLink: canvaResult.error,
        }))
        return
      }

      // Other
      const otherResult = validateAndCleanOtherSocialMediaLink(
        data.otherSocialMediaLink,
      )
      if (!otherResult.isValid) {
        setValidationErrors((prev) => ({
          ...prev,
          otherSocialMediaLink: otherResult.error,
        }))
        return
      }

      await updateCreator({
        variables: {
          id: creator.id,
          input: {
            instagramHandle: instagramResult.cleanedValue,
            tiktokHandle: tiktokResult.cleanedValue,
            canvaLink: canvaResult.cleanedValue,
            otherSocialMediaLink: otherResult.cleanedValue,
          },
        },
      })

      addNotification(
        'Social media profiles updated successfully',
        NotificationType.success,
      )
    } catch (error) {
      console.log('@error EditSocialMedia', error.message)
      addNotification(error.message, NotificationType.error)
    }
  }

  const InstagramHandleField = () => (
    <div className='sm:col-span-3'>
      <TextField
        label='Instagram Handle or Link'
        name='instagramHandle'
        placeholder='@yourusername or instagram.com/yourusername'
        error={validationErrors.instagramHandle}
        {...register('instagramHandle', {
          maxLength: {
            value: 500,
            message: 'The Instagram username or URL is too long',
          },
        })}
      />
    </div>
  )

  const TikTokHandleField = () => (
    <div className='sm:col-span-3'>
      <TextField
        label='TikTok Handle or Link'
        name='tiktokHandle'
        placeholder='@yourusername or tiktok.com/@yourusername'
        error={validationErrors.tiktokHandle}
        {...register('tiktokHandle', {
          maxLength: {
            value: 500,
            message: 'The TikTok username or URL is too long',
          },
        })}
      />
    </div>
  )

  const CanvaLinkField = () => (
    <div className='sm:col-span-3'>
      <TextField
        label='Canva Portfolio Link'
        name='canvaLink'
        placeholder='https://username.my.canva.site/portfolio-name'
        error={validationErrors.canvaLink}
        {...register('canvaLink', {
          maxLength: {
            value: 1000,
            message: 'The URL is too long',
          },
        })}
      />
    </div>
  )

  const OtherSocialMediaField = () => (
    <div className='sm:col-span-3'>
      <TextField
        label='Other Social Media Link'
        name='otherSocialMediaLink'
        placeholder='https://www.facebook.com/yourprofile'
        error={validationErrors.otherSocialMediaLink}
        {...register('otherSocialMediaLink', {
          maxLength: {
            value: 1000,
            message: 'The URL is too long',
          },
        })}
      />
    </div>
  )

  return (
    <div className='pt-10 grid grid-cols-1 gap-x-8 gap-y-8 lg:grid-cols-3'>
      <div className='px-0'>
        <div className='flex'>
          <h2 className='text-lg leading-6 font-medium text-gray-900'>
            Social Media Profiles
          </h2>
        </div>
        <p className='mt-1 text-sm text-gray-500'>
          This information is for internal use only and won't be shared with
          brands.
        </p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className='bg-white shadow ring-1 ring-gray-900/5 sm:rounded-xl lg:col-span-2'
      >
        <div className='px-4 py-6 sm:p-8'>
          <div className='grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6'>
            <div className='sm:col-span-3'>
              <InstagramHandleField />
            </div>

            <div className='sm:col-span-3'>
              <TikTokHandleField />
            </div>

            <div className='sm:col-span-3'>
              <CanvaLinkField />
            </div>

            <div className='sm:col-span-3'>
              <OtherSocialMediaField />
            </div>
          </div>
        </div>
        <div className='flex items-center justify-end gap-x-6 border-t border-gray-900/10 px-4 py-4 sm:px-8'>
          <Button
            title='Update'
            type='submit'
            loading={loading}
            disabled={loading}
          />
        </div>
      </form>
    </div>
  )
}

export { EditSocialMedia }
